import { EventCheckout } from 'store/api/event';
import { isShippedEventOrder } from 'utils/helpers';

import {
  FormValuesItems,
  AvailableRefundAmounts,
  CalculateAvailableRefundAmounts,
} from './types';

const toNum = (val: any) => Number(val) || 0;

/**
 * Calculates the maximum allowed refund amounts for tip and sale total.
 *
 * @param {EventCheckout} [data] - The event checkout data.
 * @returns {{ maxAllowedTipRefund: number, maxAllowedRefund: number, maxAllowedShippingRefund: number, maxAllowedProcessingFeeRefund: number }} - An object containing the maximum allowed refund amounts.
 */
export const calculateMaxRefundAmounts = (data?: EventCheckout) => {
  if (!data) {
    return {
      maxAllowedRefund: 0,
      maxAllowedTipRefund: 0,
      maxAllowedFreeShippingRefund: 0,
      maxAllowedProcessingFeeRefund: 0,
    };
  }

  const {
    tip = 0,
    refund = [],
    saleTotal = 0,
    shippingPrice = 0,
    isFreeShipping = false,
    processingFeesAmount = 0,
  } = data;

  const totals = refund.reduce(
    (acc, refundItem) => {
      refundItem.refundPayments?.forEach((payment) => {
        acc.refunded += toNum(payment.amount || 0);
        acc.tip += toNum(payment.tip || 0);
        acc.freeShipping += toNum(payment.freeShipping || 0);
        acc.processingFee += toNum(payment.processingFee || 0);
      });
      return acc;
    },
    { tip: 0, refunded: 0, freeShipping: 0, processingFee: 0 }
  );

  const isShippedOrder = isShippedEventOrder(data);

  const maxAllowedFreeShippingRefund =
    isFreeShipping && !isShippedOrder
      ? toNum(shippingPrice) - totals.freeShipping
      : 0;

  const nonRefundableShippingAmount =
    isFreeShipping || isShippedOrder ? toNum(shippingPrice) : 0;

  const maxAllowedRefund = toNum(
    (
      (saleTotal || 0) -
      (tip || 0) -
      nonRefundableShippingAmount -
      (processingFeesAmount || 0) -
      (data.taxAmount || 0) -
      (totals.refunded || 0)
    ).toFixed(2)
  );

  return {
    maxAllowedRefund,
    maxAllowedTipRefund: tip - totals.tip,
    maxAllowedFreeShippingRefund: Math.max(0, maxAllowedFreeShippingRefund),
    maxAllowedProcessingFeeRefund: processingFeesAmount - totals.processingFee,
  };
};

/**
 * Calculates the total amount of selected items.
 *
 * @param {FormValuesItems[]} items - An array of form values items.
 * @returns {number} - The total amount of selected items.
 */
export const calculateSelectedItemsAmount = (
  items: FormValuesItems[]
): number =>
  items.reduce(
    (total, item) =>
      item.selected ? total + item.price * item.quantity : total,
    0
  );

/**
 * Calculates refund data including refund price, refundable total, and selected items amount.
 *
 * @param {object} params - An object containing parameters.
 * @param {EventCheckout} [params.data] - The event checkout data.
 * @param {FormValuesItems[]} params.items - An array of form values items.
 * @returns {{ refundPrice: number, refundableTotal: number, selectedItemsAmount: number }} - An object containing refund data.
 */
export const calculateRefundData = ({
  data,
  items,
}: {
  data?: EventCheckout;
  items: FormValuesItems[];
}): {
  refundPrice: number;
  refundableTotal: number;
  selectedItemsAmount: number;
} => {
  const selectedItemsAmount = calculateSelectedItemsAmount(items);

  const refundableTotal = selectedItemsAmount + (data?.taxAmount || 0);

  return { refundableTotal, selectedItemsAmount, refundPrice: refundableTotal };
};

/**
 * Formats a number to two decimal places.
 *
 * @param {number} num - The number to format.
 * @returns {number} The formatted number with two decimal places.
 */
export const formatNumber = (num: number): number => Number(num.toFixed(2));

/**
 * Calculates the available amounts for a refund based on the checkout data, selected items,
 * existing return payments, and maximum allowed refund amounts.
 *
 * @param {CalculateAvailableRefundAmounts} params - An object containing the parameters for the calculation.
 * @param {EventCheckout | undefined} params.data - The event checkout data.
 * @param {FormValuesItems[]} params.items - The list of items being refunded.
 * @param {string | undefined} params.excludeId - The ID of a return payment to exclude from the calculation.
 * @param {ReturnPayment[]} params.returnPayments - The list of existing return payments.
 * @param {number | undefined} params.maxAllowedRefund - The maximum allowed amount for refund.
 * @param {number | undefined} params.maxAllowedTipRefund - The maximum allowed amount for tip refund.
 * @param {number | undefined} params.maxAllowedShippingRefund - The maximum allowed amount for shipping refund.
 * @param {number | undefined} params.maxAllowedProcessingFeeRefund - The maximum allowed amount for processing fee refund.
 * @returns {AvailableRefundAmounts} An object containing the total refundable amount, remaining amounts, and the amount of the selected items.
 */
export const calculateAvailableRefundAmounts = ({
  data,
  items,
  excludeId,
  returnPayments,
  maxAllowedRefund = 0,
  maxAllowedTipRefund = 0,
  maxAllowedFreeShippingRefund = 0,
  maxAllowedProcessingFeeRefund = 0,
}: CalculateAvailableRefundAmounts): AvailableRefundAmounts => {
  const selectedItemsAmount = calculateSelectedItemsAmount(items);
  const availableTax = toNum(data?.taxAmount);
  const totalRefundable = formatNumber(maxAllowedRefund + availableTax);

  const totals = (returnPayments || []).reduce(
    (acc, payment) => {
      if (excludeId && payment.id === excludeId) {
        return acc;
      }

      acc.payments += toNum(payment.amount);
      acc.tip += toNum(payment.tip);
      acc.freeShipping += toNum(payment.freeShipping);
      acc.processingFee += toNum(payment.processingFee);
      return acc;
    },
    { tip: 0, payments: 0, freeShipping: 0, processingFee: 0 }
  );

  return {
    totalRefundable,
    selectedItemsAmount,
    remainingAmount: formatNumber(totalRefundable - totals.payments),
    remainingTipAmount: formatNumber(maxAllowedTipRefund - totals.tip),
    remainingFreeShippingAmount: formatNumber(
      maxAllowedFreeShippingRefund - totals.freeShipping
    ),
    remainingProcessingFeeAmount: formatNumber(
      maxAllowedProcessingFeeRefund - totals.processingFee
    ),
  };
};
